import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { getBottomPadding, getTopPadding } from '../../../utils/sessionStorage.ts'
import { InfiniteStatusEnum } from '../../../repository/infinite.ts'
import { fetchUserDetails } from '../../../repository/misc.ts'
import { trackEvent } from '../../../utils/appBridge.ts'
import { EventNames } from '../../../types/misc.types.ts'
import { InfiniteHeroVariant, InfiniteLandingPageState } from '../types/infiniteLanding.types.ts'

export function useInfiniteLandingPageState(): InfiniteLandingPageState {
  const topPadding = getTopPadding()
  const bottomPadding = getBottomPadding()
  const firstFoldMinHeight = `calc(85dvh - ${topPadding}px - ${bottomPadding}px)`

  const [hasJoinedWaitlistFromCta, setHasJoinedWaitlistFromCta] = useState(false)
  const hasTrackedScreenLoadedRef = useRef(false)
  const statusParam = new URLSearchParams(window?.location?.search ?? '').get('status')
  const onboardingStatus = Object.values(InfiniteStatusEnum).includes(statusParam as InfiniteStatusEnum)
    ? (statusParam as InfiniteStatusEnum)
    : undefined

  const { data: userDetailsData } = useQuery({
    queryKey: ['userDetails'],
    queryFn: fetchUserDetails,
    enabled: onboardingStatus === InfiniteStatusEnum.ONBOARDED || onboardingStatus === InfiniteStatusEnum.ACCESS_OPEN
  })
  const isRequestCallbackFlow =
    onboardingStatus === InfiniteStatusEnum.ELIGIBLE_WITH_CALLBACK ||
    onboardingStatus === InfiniteStatusEnum.JOINED_WAITLIST_WITH_CALLBACK
  const hasJoinedWaitlist =
    hasJoinedWaitlistFromCta ||
    onboardingStatus === InfiniteStatusEnum.JOINED_WAITLIST ||
    onboardingStatus === InfiniteStatusEnum.JOINED_WAITLIST_WITH_CALLBACK
  const hasOnboardedStatus = onboardingStatus === InfiniteStatusEnum.ACCESS_OPEN
  const hasOnboardedClubStatus = onboardingStatus === InfiniteStatusEnum.ONBOARDED

  useEffect(() => {
    if (hasTrackedScreenLoadedRef.current || !onboardingStatus) {
      return
    }
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        eventId: 'infinite_landing_reached',
        screenType: 'INFINITE_LANDING_PAGE',
        responseName: onboardingStatus
      }
    })
    hasTrackedScreenLoadedRef.current = true
  }, [onboardingStatus])

  let heroVariant: InfiniteHeroVariant = 'default'
  if (hasOnboardedClubStatus) {
    heroVariant = 'onboarded-club'
  } else if (hasOnboardedStatus) {
    heroVariant = 'onboarded-open'
  } else if (hasJoinedWaitlist) {
    heroVariant = 'waitlist-success'
  }

  return {
    topPadding,
    bottomPadding,
    firstFoldMinHeight,
    heroVariant,
    userFirstName: userDetailsData?.name ?? '',
    isInitialLoading: false,
    onJoinWaitlistSuccess: () => setHasJoinedWaitlistFromCta(true),
    isRequestCallbackFlow,
    onboardingStatus
  }
}
